import React, { useEffect, useState } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  FaCalendarCheck,
  FaClock,
  FaCheckCircle,
  FaHourglassHalf,
  FaThumbsUp,
  FaPercent,
} from 'react-icons/fa';
import analyticsApi from '../../../services/analyticsApi';
import {
  StatCard,
  ChartCard,
  STATUS_COLORS,
  statusDoughnutData,
  doughnutOptions,
  lineOptions,
} from './analyticsShared';

export default function FarmerAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await analyticsApi.getMyBookings();
        if (mounted) setData(res.analytics);
      } catch (err) {
        /* interceptor toasts */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading analytics…</div>;
  }

  if (!data || data.total === 0) {
    return (
      <div className="max-w-xl mx-auto mt-10 bg-white border border-gray-200 rounded-xl p-8 text-center">
        <FaCalendarCheck className="mx-auto text-3xl text-primary mb-3" />
        <h2 className="text-lg font-semibold text-gray-800">No bookings yet</h2>
        <p className="text-sm text-gray-500 mt-2">
          {data?.viewerRole === 'specialist'
            ? 'Once you receive consultation bookings, your analytics will appear here.'
            : 'Once you book consultations with specialists, your booking analytics will appear here.'}
        </p>
      </div>
    );
  }

  const { status, type, monthly } = data;

  const monthlyData = {
    labels: monthly.map((m) => m.label),
    datasets: [
      {
        data: monthly.map((m) => m.count),
        borderColor: '#1e5c33',
        backgroundColor: 'rgba(30,92,51,0.12)',
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        pointRadius: 3,
      },
    ],
  };

  const typeData = {
    labels: ['Remote', 'On-site'],
    datasets: [
      {
        data: [type.remote || 0, type['on-site'] || 0],
        backgroundColor: ['#7caf64', '#1e5c33'],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-primary">My Booking Analytics</h1>
        <p className="text-sm text-gray-500">Your consultations &amp; bookings at a glance</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Total Bookings" value={data.total} icon={<FaCalendarCheck />} />
        <StatCard label="Pending" value={status.pending} icon={<FaHourglassHalf />} accent="text-amber-600" />
        <StatCard label="Accepted" value={status.accepted} icon={<FaThumbsUp />} accent="text-blue-600" />
        <StatCard label="Completed" value={status.completed} icon={<FaCheckCircle />} accent="text-primary" />
        <StatCard label="Upcoming" value={data.upcoming} icon={<FaClock />} accent="text-secondary" />
        <StatCard label="Completion" value={`${data.completionRate}%`} icon={<FaPercent />} accent="text-primary" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Status Breakdown">
          <div style={{ height: 240 }}>
            <Doughnut data={statusDoughnutData(status)} options={doughnutOptions} />
          </div>
        </ChartCard>

        <ChartCard title="Bookings Over Time (6 months)" className="lg:col-span-2">
          <div style={{ height: 240 }}>
            <Line data={monthlyData} options={lineOptions} />
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Consultation Type">
          <div style={{ height: 220 }}>
            <Doughnut data={typeData} options={doughnutOptions} />
          </div>
        </ChartCard>

        {/* Per-counterparty table (specialists for a farmer/user, clients for a specialist) */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Bookings by {data.counterpartyLabel}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">{data.counterpartyLabel}</th>
                  <th className="text-left px-4 py-2 font-medium">Total</th>
                  <th className="text-left px-4 py-2 font-medium">Accepted</th>
                  <th className="text-left px-4 py-2 font-medium">Pending</th>
                  <th className="text-left px-4 py-2 font-medium">Completed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.breakdown.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-400">Nothing here yet.</td>
                  </tr>
                )}
                {data.breakdown.map((s) => (
                  <tr key={s.counterpartyId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-800">
                      <div className="font-medium">{s.name || 'Unknown'}</div>
                      {(s.specialty || s.role) && (
                        <div className="text-xs text-gray-400">{s.specialty || s.role}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700">{s.total}</td>
                    <td className="px-4 py-2" style={{ color: STATUS_COLORS.accepted }}>{s.accepted}</td>
                    <td className="px-4 py-2" style={{ color: STATUS_COLORS.pending }}>{s.pending}</td>
                    <td className="px-4 py-2" style={{ color: STATUS_COLORS.completed }}>{s.completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
